import { getAuth } from "@clerk/nextjs/server";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import dynamic from "next/dynamic";
import FullContainerLoader from "@/components/common/loaders/fullContainerLoader";
import CustomBookingThemeProvider from "@/config/customBookingThemeProvider";
import { StripeProvider } from "@/config/stripe";
import {
  MedspaOnlineStoreDataDocument,
  MedspaOnlineStoreDataQuery,
  MedspaOnlineStoreDataQueryResult,
} from "@/graphql/queries/onlineStore/medspaOnlineStoreData.graphql.types";
import { addApolloState, initializeApollo } from "@/lib/apollo";
import { calculateBookingCustomizationEnabled } from "@/utils";
import { OnlineStoreTab } from "@/views/online-store/onlineStoreView";

const OnlineStoreView = dynamic(
  () => import("@/views/online-store/onlineStoreView"),
  {
    ssr: false,
    loading: () => <FullContainerLoader />,
  }
);

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

export default function OnlineStore({ medspaInfo, initialTab }: Props) {
  const customizationEnabled = calculateBookingCustomizationEnabled(medspaInfo);
  const { customizationPrimaryColor } = medspaInfo.configuration;

  return (
    <CustomBookingThemeProvider
      customizationEnabled={customizationEnabled}
      customizationPrimaryColor={customizationPrimaryColor}
    >
      <StripeProvider>
        <OnlineStoreView
          medspaData={
            medspaInfo as MedspaOnlineStoreDataQuery["medspa"][number]
          }
          initialTab={initialTab}
        />
      </StripeProvider>
    </CustomBookingThemeProvider>
  );
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const { medspaSlug, tab } = ctx.query;

  const tabFromQuery = tab as string;

  const { getToken: getClerkToken } = getAuth(ctx.req);
  const apolloClient = initializeApollo(null, getClerkToken);

  const { data } = await apolloClient.query<
    MedspaOnlineStoreDataQueryResult["data"]
  >({
    query: MedspaOnlineStoreDataDocument,
    variables: {
      slug: medspaSlug,
    },
    context: {
      noAuth: true,
    },
  });

  if (!data.medspa.length) {
    return {
      notFound: true,
    };
  }

  const medspaInfo = data.medspa[0];

  const initialTab: OnlineStoreTab = tabFromQuery
    ? OnlineStoreTab[tabFromQuery.toUpperCase()]
    : OnlineStoreTab.PACKAGES;

  return addApolloState(apolloClient, {
    props: {
      medspaInfo,
      initialTab,
    },
  });
};
